import { useEffect, useMemo, useState } from "react"
import type { ListGdeltBiothreatSignalsGdeltBiothreatSignalsGetParams } from "@phc/common/gen/threatAnalyticsGen"
import {
  getDates,
  isRelative,
  safelyParseDate,
  toCalendarDateString,
  type DynamicDateRange,
} from "../../utils/dates"
import { usePrecomputedBiothreatSignals } from "./PrecomputedBiothreatSignalsContext"

const RELATIVE_LOOKBACK_REFRESH_MS = 60_000

/** Recompute rolling lookback windows while a relative preset is selected. */
const useRelativeLookbackTick = (dateRange: DynamicDateRange) => {
  const [tick, setTick] = useState(0)

  useEffect(() => {
    if (!isRelative(dateRange)) {
      return
    }

    const id = window.setInterval(
      () => setTick(value => value + 1),
      RELATIVE_LOOKBACK_REFRESH_MS
    )
    return () => window.clearInterval(id)
  }, [dateRange])

  return tick
}

const toGdeltDateFilterIsoRange = (dateRange: DynamicDateRange) => {
  const dates = getDates(dateRange)
  const start = safelyParseDate(dates[0])
  const end = safelyParseDate(dates[1])

  if (!start?.isValid() || !end?.isValid()) {
    return null
  }

  if (isRelative(dateRange)) {
    return {
      start: start.toISOString(),
      end: end.toISOString(),
    }
  }

  return {
    start: start.startOf("day").toISOString(),
    end: end.endOf("day").toISOString(),
  }
}

type GdeltBiothreatDateFilterParams = Pick<
  ListGdeltBiothreatSignalsGdeltBiothreatSignalsGetParams,
  "active_date_range_started_at" | "active_date_range_ended_at"
>

export type { GdeltBiothreatDateFilterParams }

const toNwssDateRange = (dateRange: DynamicDateRange) => {
  const [start, end] = getDates(dateRange)
  const startDate = toCalendarDateString(start)
  const endDate = toCalendarDateString(end)

  if (!startDate || !endDate) {
    return {}
  }

  return {
    start_date: startDate,
    end_date: endDate,
  }
}

export const useGdeltDateFilterParams = (): GdeltBiothreatDateFilterParams => {
  const { dateRange } = usePrecomputedBiothreatSignals()
  const relativeTick = useRelativeLookbackTick(dateRange)

  return useMemo((): GdeltBiothreatDateFilterParams => {
    void relativeTick
    const range = toGdeltDateFilterIsoRange(dateRange)
    if (!range) {
      return {}
    }
    return {
      active_date_range_started_at: range.start,
      active_date_range_ended_at: range.end,
    }
  }, [dateRange, relativeTick])
}

export const useNwssDateParams = () => {
  const { dateRange } = usePrecomputedBiothreatSignals()
  const relativeTick = useRelativeLookbackTick(dateRange)

  return useMemo(() => {
    void relativeTick
    return toNwssDateRange(dateRange)
  }, [dateRange, relativeTick])
}
